/**
 * useNewEntitiesTracking - Hook para tracking de entidades recién creadas
 *
 * @version 1.0
 * @module core/entity-registry
 *
 * @description
 * Hook genérico para rastrear qué entidades fueron recién creadas en la sesión actual.
 * Usado para determinar si una entidad debe ser eliminada de BD al cancelar.
 *
 * @remarks
 * Reemplaza el tracking específico por tipo (newlyCreatedTreeIds, etc.)
 * con un sistema genérico que funciona para cualquier entidad.
 */

import { useRef, useCallback } from 'react';

/**
 * Estructura de tracking por tipo de entidad
 */
interface EntityTracking {
  /**
   * Mapa de tipo de entidad → Set de IDs recién creados
   */
  tracking: Map<string, Set<string>>;
}

/**
 * Hook result
 */
export interface UseNewEntitiesTrackingResult {
  /**
   * Registra una entidad como recién creada
   *
   * @param entityType - Tipo de entidad ('tree', 'container', etc.)
   * @param id - ID de la entidad
   */
  track: (entityType: string, id: string) => void;

  /**
   * Verifica si una entidad está marcada como recién creada
   *
   * @param entityType - Tipo de entidad
   * @param id - ID de la entidad
   * @returns true si la entidad fue recién creada
   */
  isTracked: (entityType: string, id: string) => boolean;

  /**
   * Deja de rastrear una entidad (cuando se guarda explícitamente)
   *
   * @param entityType - Tipo de entidad
   * @param id - ID de la entidad
   */
  untrack: (entityType: string, id: string) => void;

  /**
   * Limpia todo el tracking de un tipo de entidad
   *
   * @param entityType - Tipo de entidad
   */
  clearType: (entityType: string) => void;

  /**
   * Limpia todo el tracking
   */
  clearAll: () => void;
}

/**
 * Hook para tracking de entidades recién creadas
 *
 * @example
 * ```tsx
 * function MapLayout() {
 *   const { track, isTracked, untrack } = useNewEntitiesTracking();
 *
 *   const handleCreate = async (type: string, entity: any) => {
 *     const saved = await api.create(entity);
 *     track(type, saved.id); // Marcar como recién creada
 *   };
 *
 *   const handleCancel = async (type: string, id: string) => {
 *     if (isTracked(type, id)) {
 *       await api.delete(id); // Eliminar de BD
 *       untrack(type, id);
 *     }
 *   };
 * }
 * ```
 */
export function useNewEntitiesTracking(): UseNewEntitiesTrackingResult {
  const trackingRef = useRef<EntityTracking>({
    tracking: new Map(),
  });

  const track = useCallback((entityType: string, id: string): void => {
    const { tracking } = trackingRef.current;

    if (!tracking.has(entityType)) {
      tracking.set(entityType, new Set());
    }

    tracking.get(entityType)!.add(id);
  }, []);

  const isTracked = useCallback((entityType: string, id: string): boolean => {
    const { tracking } = trackingRef.current;
    return tracking.get(entityType)?.has(id) ?? false;
  }, []);

  const untrack = useCallback((entityType: string, id: string): void => {
    const { tracking } = trackingRef.current;
    tracking.get(entityType)?.delete(id);
  }, []);

  const clearType = useCallback((entityType: string): void => {
    const { tracking } = trackingRef.current;
    tracking.delete(entityType);
  }, []);

  const clearAll = useCallback((): void => {
    trackingRef.current.tracking.clear();
  }, []);

  return {
    track,
    isTracked,
    untrack,
    clearType,
    clearAll,
  };
}
